package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/** Event discovery card used by compact lists, desktop grids, and the featured-event slot. */
@Composable
fun EventCard(
    title: String,
    dateTime: String,
    venue: String,
    actionLabel: String,
    imageContentDescription: String,
    onAction: () -> Unit,
    modifier: Modifier = Modifier,
    coverUrl: String? = null,
    badgeLabel: String? = null,
    actionEnabled: Boolean = true,
    featured: Boolean = false,
    imageAspectRatio: Float = 16f / 9f,
    /** Localized a11y label for the share button; the button only renders when [onShare] is set. */
    shareContentDescription: String? = null,
    onShare: (() -> Unit)? = null,
    /** Whole-card tap (e.g. open the event detail page). Independent of the action button. */
    onClick: (() -> Unit)? = null,
    /** Trailing glyph on the action button — "open_in_new" for outlinks, "chevron_right" for in-app. */
    actionTrailingIcon: String? = "open_in_new",
) {
    AppCard(modifier = modifier.fillMaxWidth(), onClick = onClick) {
        Column {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(imageAspectRatio),
                contentAlignment = Alignment.TopEnd,
            ) {
                BusinessCover(
                    imageUrl = coverUrl,
                    contentDescription = imageContentDescription,
                    iconName = "event",
                )
                if (badgeLabel != null) {
                    StatusBadge(
                        label = badgeLabel,
                        modifier = Modifier.padding(AppTheme.spacing.sm),
                    )
                }
            }
            Column(
                modifier = Modifier.padding(if (featured) AppTheme.spacing.xl else AppTheme.spacing.lg),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
            ) {
                Text(
                    text = dateTime,
                    style = AppTheme.typography.labelSm,
                    color = AppTheme.colors.primary,
                )
                Text(
                    text = title,
                    style = if (featured) AppTheme.typography.headlineLg else AppTheme.typography.headlineMd,
                    color = AppTheme.colors.onSurface,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )
                Row(
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    AppIcon(
                        name = "location_on",
                        size = AppTheme.sizes.iconSm,
                        tint = AppTheme.colors.onSurfaceVariant,
                    )
                    Text(
                        text = venue,
                        style = AppTheme.typography.bodySm,
                        color = AppTheme.colors.onSurfaceVariant,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis,
                    )
                }
                Row(
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    AppButton(
                        text = actionLabel,
                        onClick = onAction,
                        enabled = actionEnabled,
                        variant = if (featured) AppButtonVariant.Primary else AppButtonVariant.Secondary,
                        trailingIcon = actionTrailingIcon,
                        modifier = Modifier.weight(1f),
                    )
                    if (onShare != null && shareContentDescription != null) {
                        AppIconButton(
                            iconName = "share",
                            contentDescription = shareContentDescription,
                            onClick = onShare,
                        )
                    }
                }
            }
        }
    }
}

@Preview
@Composable
private fun EventCardPreview() {
    AppTheme {
        EventCard(
            title = "Nowruz Gala",
            dateTime = "FRI, MAR 20 • 6:30 PM",
            venue = "Liberty Grand, Toronto",
            actionLabel = "Tickets on organizer site",
            imageContentDescription = "Event cover",
            onAction = {},
            badgeLabel = "Featured",
        )
    }
}
